import { type FC, useCallback, useState } from "react";

import { type BlackboardInstance } from "../blackboardModels";
import { ChannelPickerDialog } from "../ChannelPickerDialog";
import { ChannelTag } from "../ChannelTag";
import { useOrgChannels } from "../useOrgChannels";
import { type TeamsBindLink } from "./helpers";
import { mintTeamsBindLink, unbindTeamsChannel } from "./teamsBindingActions";
import { TeamsBindLinkPanel } from "./TeamsBindLinkPanel";

// ---------------------------------------------------------------------------
// ChannelManager — current channels (leave), join, Teams bind/unbind
// ---------------------------------------------------------------------------

/**
 * Lifted verbatim from the former InfoTab god component. Renders each channel
 * as a tag with leave/bind/unbind actions, plus a join dialog trigger.
 *
 * `onOpenChannelWorkspace` is the channel→sheet pivot affordance; the sheet
 * keeps it as a prop rather than re-deriving the navigation here.
 */
export const ChannelManager: FC<{
  instanceId: string;
  channels: BlackboardInstance["channels"];
  onChannelsChange?: () => void;
  onOpenChannelWorkspace?: (channelId: string) => void;
  teamsChatSdkEnabled: boolean;
}> = ({
  instanceId,
  channels,
  onChannelsChange,
  onOpenChannelWorkspace,
  teamsChatSdkEnabled,
}) => {
  const [leaving, setLeaving] = useState<string | null>(null);
  const [binding, setBinding] = useState<string | null>(null);
  const [bindingLink, setBindingLink] = useState<TeamsBindLink | null>(null);
  const [unbinding, setUnbinding] = useState<string | null>(null);
  const [locallyUnboundChannelIds, setLocallyUnboundChannelIds] = useState<
    Set<string>
  >(() => new Set());
  const [showPicker, setShowPicker] = useState(false);
  const { byId: orgChannelsById } = useOrgChannels();

  // An actor can be in at most one shared channel; the backend implements
  // "join another" as a swap (leave + join). Surface that in the labels.
  const currentSharedChannel =
    channels.find((ch) => ch.kind === "shared") ?? null;
  const currentSharedChannelName = currentSharedChannel
    ? (orgChannelsById.get(currentSharedChannel.id)?.displayName ??
      currentSharedChannel.displayName)
    : null;

  const handleLeave = useCallback(
    async (channelId: string) => {
      setLeaving(channelId);
      try {
        await fetch(`/user/instances/${instanceId}/channels/${channelId}`, {
          credentials: "include",
          method: "DELETE",
        });
        onChannelsChange?.();
      } finally {
        setLeaving(null);
      }
    },
    [instanceId, onChannelsChange],
  );

  const handleBind = useCallback(async (channelId: string) => {
    setBinding(channelId);
    try {
      const result = await mintTeamsBindLink(channelId);
      if (!result.ok) {
        throw new Error(`Failed to mint Teams binding token`);
      }
      setBindingLink(result.link);
    } finally {
      setBinding(null);
    }
  }, []);

  const handleUnbind = useCallback(
    async (channelId: string) => {
      setUnbinding(channelId);
      try {
        const result = await unbindTeamsChannel(channelId);
        if (!result.ok) {
          throw new Error(`Failed to unbind Teams channel`);
        }
        setBindingLink(null);
        setLocallyUnboundChannelIds((prev) => {
          const next = new Set(prev);
          next.add(channelId);
          return next;
        });
        onChannelsChange?.();
      } finally {
        setUnbinding(null);
      }
    },
    [onChannelsChange],
  );

  return (
    <div className="flex flex-col gap-2">
      {channels.length === 0 ? (
        <span className="font-mono text-[10px] font-bold uppercase tracking-[0.25em] text-slate-500">
          [ NO CHANNELS ]
        </span>
      ) : (
        <div className="flex flex-wrap gap-1.5">
          {channels.map((ch) => {
            const summary = orgChannelsById.get(ch.id);
            // Private channels: a single clickable tag that opens the 1:1
            // conversation in the channel sheet (which adapts to private kind).
            // Previously these were non-interactive labels, making the actor's
            // private conversation unreachable from the Identity tab.
            if (!summary || ch.kind === "private") {
              const displayName = summary?.displayName ?? ch.displayName;
              return (
                <button
                  key={ch.id}
                  type="button"
                  disabled={!onOpenChannelWorkspace}
                  onClick={() => onOpenChannelWorkspace?.(ch.id)}
                  className="border border-slate-300 bg-slate-200/40 px-2 py-0.5 font-mono text-[10px] font-bold uppercase tracking-[0.2em] text-slate-500 transition-colors hover:border-emerald-500/60 hover:text-emerald-700 disabled:cursor-default disabled:opacity-60 dark:border-slate-700/60 dark:bg-slate-800/40 dark:hover:border-emerald-400/50 dark:hover:text-emerald-300"
                >
                  #{displayName}
                </button>
              );
            }
            const hasActiveTeamsBinding = Boolean(
              summary.activeTeamsBindingId &&
              !locallyUnboundChannelIds.has(ch.id),
            );
            return (
              <div key={ch.id} className="flex items-center gap-1">
                <ChannelTag
                  channel={summary}
                  onLeave={() => void handleLeave(ch.id)}
                  leaving={leaving === ch.id}
                />
                {summary.kind === "shared" && onOpenChannelWorkspace && (
                  <button
                    className="border border-emerald-500/50 px-1.5 py-0.5 font-mono text-[9px] font-bold uppercase tracking-[0.16em] text-emerald-700/90 hover:border-emerald-500/80 dark:border-emerald-400/30 dark:text-emerald-300/80 dark:hover:border-emerald-400/70"
                    onClick={() => onOpenChannelWorkspace(ch.id)}
                    type="button"
                  >
                    Workspace
                  </button>
                )}
                {teamsChatSdkEnabled && hasActiveTeamsBinding && (
                  <span className="border border-cyan-500/40 px-1.5 py-0.5 font-mono text-[9px] font-bold uppercase tracking-[0.16em] text-cyan-700/90 dark:border-cyan-400/25 dark:text-cyan-300/80">
                    {summary.teamsBindingChannelKind === "shared_bound"
                      ? "Shared + Teams"
                      : "Teams"}
                  </span>
                )}
                {teamsChatSdkEnabled && (
                  <>
                    {hasActiveTeamsBinding ? (
                      <button
                        className="border border-rose-500/50 px-1.5 py-0.5 font-mono text-[9px] font-bold uppercase tracking-[0.16em] text-rose-700/90 hover:border-rose-500/80 dark:border-rose-400/30 dark:text-rose-300/80 dark:hover:border-rose-400/70"
                        disabled={unbinding === ch.id}
                        onClick={() => void handleUnbind(ch.id)}
                        type="button"
                      >
                        {unbinding === ch.id ? "..." : "Unbind"}
                      </button>
                    ) : (
                      <button
                        className="border border-cyan-500/50 px-1.5 py-0.5 font-mono text-[9px] font-bold uppercase tracking-[0.16em] text-cyan-700/90 hover:border-cyan-500/80 dark:border-cyan-400/30 dark:text-cyan-300/80 dark:hover:border-cyan-400/70"
                        disabled={binding === ch.id}
                        onClick={() => void handleBind(ch.id)}
                        type="button"
                      >
                        {binding === ch.id ? "..." : "Teams"}
                      </button>
                    )}
                  </>
                )}
              </div>
            );
          })}
        </div>
      )}
      {bindingLink && <TeamsBindLinkPanel link={bindingLink} />}
      <button
        className="self-start border border-emerald-400/30 bg-slate-100/60 px-2 py-1 font-mono text-[10px] font-bold uppercase tracking-[0.2em] text-emerald-700/80 transition-colors hover:border-emerald-400/60 hover:text-emerald-700 dark:bg-slate-900/40 dark:text-emerald-300/80 dark:hover:text-emerald-300"
        onClick={() => setShowPicker(true)}
        type="button"
      >
        {currentSharedChannelName
          ? "[ ⇄ CHANGE SHARED CHANNEL ]"
          : "[ + JOIN CHANNEL ]"}
      </button>
      {showPicker && (
        <ChannelPickerDialog
          instanceId={instanceId}
          memberChannelIds={new Set(channels.map((ch) => ch.id))}
          currentSharedChannelName={currentSharedChannelName}
          onClose={() => setShowPicker(false)}
          onJoined={() => {
            setShowPicker(false);
            onChannelsChange?.();
          }}
        />
      )}
    </div>
  );
};
